import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import
{
  FiUsers, FiPlus, FiEdit2, FiUserX, FiX, FiSave,
  FiShield, FiMail, FiUser, FiLock, FiAlertTriangle,
  FiBriefcase, FiCheck, FiSearch
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { userAPI, companyAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const DEFAULT_ROLES = [
  { key: 'Super Admin', label: 'Super Admin', desc: 'Full system access, all companies', superAdminOnly: true },
  { key: 'Admin', label: 'Admin', desc: 'Full system access' },
  { key: 'Manager', label: 'Manager', desc: 'Manage documents & contacts' },
  { key: 'Operator', label: 'Operator', desc: 'Create and edit documents' },
  { key: 'Viewer', label: 'Viewer', desc: 'Read-only access' },
];

const emptyUser = {
  username: '',
  email: '',
  password: '',
  full_name: '',
  roles: [],
  company_ids: [],
};

// ── Styles ──────────────────────────────────────────────────────────────────────

const styles = {
  container: {
    fontFamily: 'Inter, sans-serif',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 28,
    flexWrap: 'wrap',
    gap: 16,
  },
  title: {
    fontFamily: 'Poppins, sans-serif',
    fontSize: 28,
    fontWeight: 800,
    color: '#064e3b',
    margin: 0,
  },
  subtitle: {
    fontSize: 14,
    color: '#6b7280',
    marginTop: 4,
  },
  addBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '12px 24px',
    background: 'linear-gradient(135deg, #059669, #10b981)',
    color: 'white',
    border: 'none',
    borderRadius: 12,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    boxShadow: '0 4px 15px rgba(16, 185, 129, 0.3)',
    fontFamily: 'Poppins, sans-serif',
    whiteSpace: 'nowrap',
  },
  searchWrapper: {
    position: 'relative',
    maxWidth: 400,
    marginBottom: 20,
  },
  searchIcon: {
    position: 'absolute',
    left: 14,
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#059669',
  },
  searchInput: {
    width: '100%',
    padding: '12px 14px 12px 44px',
    border: '2px solid rgba(16, 185, 129, 0.2)',
    borderRadius: 12,
    fontSize: 14,
    outline: 'none',
    background: 'rgba(255,255,255,0.8)',
    backdropFilter: 'blur(8px)',
    transition: 'all 0.2s ease',
    fontFamily: 'Inter, sans-serif',
  },
  glassCard: {
    background: 'rgba(255, 255, 255, 0.7)',
    backdropFilter: 'blur(20px)',
    borderRadius: 20,
    border: '1px solid rgba(255, 255, 255, 0.5)',
    boxShadow: '0 8px 32px rgba(6, 78, 59, 0.08)',
    overflow: 'hidden',
  },
  table: {
    width: '100%',
    borderCollapse: 'separate',
    borderSpacing: 0,
  },
  th: {
    padding: '14px 16px',
    textAlign: 'left',
    fontSize: 12,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 0.8,
    color: '#047857',
    background: 'rgba(240, 253, 244, 0.8)',
    borderBottom: '2px solid rgba(16, 185, 129, 0.15)',
  },
  td: {
    padding: '14px 16px',
    fontSize: 14,
    color: '#1f2937',
    borderBottom: '1px solid rgba(16, 185, 129, 0.08)',
    verticalAlign: 'middle',
  },
  actionBtn: {
    width: 34,
    height: 34,
    borderRadius: 8,
    border: 'none',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease',
    marginRight: 6,
  },
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(6, 78, 59, 0.5)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    background: 'white',
    borderRadius: 24,
    padding: '32px',
    width: '95%',
    maxWidth: 600,
    maxHeight: '90vh',
    overflowY: 'auto',
    boxShadow: '0 25px 80px rgba(0, 0, 0, 0.2)',
    position: 'relative',
  },
  modalTitle: {
    fontFamily: 'Poppins, sans-serif',
    fontSize: 22,
    fontWeight: 700,
    color: '#064e3b',
    marginBottom: 24,
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  fieldGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
    gap: 16,
  },
  fieldLabel: {
    display: 'block',
    fontSize: 12,
    fontWeight: 600,
    color: '#047857',
    marginBottom: 6,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  fieldInput: {
    width: '100%',
    padding: '10px 14px',
    border: '2px solid rgba(16, 185, 129, 0.2)',
    borderRadius: 10,
    fontSize: 14,
    outline: 'none',
    transition: 'all 0.2s ease',
    background: '#f0fdf4',
    fontFamily: 'Inter, sans-serif',
    boxSizing: 'border-box',
  },
  fieldSelect: {
    width: '100%',
    padding: '10px 14px',
    border: '2px solid rgba(16, 185, 129, 0.2)',
    borderRadius: 10,
    fontSize: 14,
    outline: 'none',
    background: '#f0fdf4',
    fontFamily: 'Inter, sans-serif',
    cursor: 'pointer',
    boxSizing: 'border-box',
  },
  saveBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '12px 32px',
    background: 'linear-gradient(135deg, #059669, #10b981)',
    color: 'white',
    border: 'none',
    borderRadius: 12,
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
    boxShadow: '0 4px 15px rgba(16, 185, 129, 0.3)',
    fontFamily: 'Poppins, sans-serif',
  },
  cancelBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '12px 24px',
    background: 'transparent',
    color: '#6b7280',
    border: '2px solid #e5e7eb',
    borderRadius: 12,
    fontSize: 15,
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: 'Poppins, sans-serif',
  },
  roleBadge: {
    display: 'inline-block',
    padding: '4px 10px',
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 600,
    marginRight: 6,
    marginBottom: 4,
  },
  statusBadge: {
    display: 'inline-block',
    padding: '4px 12px',
    borderRadius: 20,
    fontSize: 12,
    fontWeight: 600,
  },
  roleCheckbox: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    padding: '12px 14px',
    borderRadius: 12,
    border: '2px solid rgba(16, 185, 129, 0.15)',
    background: '#f0fdf4',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  roleCheckboxActive: {
    borderColor: '#10b981',
    background: 'rgba(16, 185, 129, 0.08)',
    boxShadow: '0 0 0 3px rgba(16, 185, 129, 0.1)',
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
    color: '#9ca3af',
  },
};

const roleColors = {
  'Super Admin': { bg: '#fce7f3', color: '#9d174d' },
  'Admin': { bg: '#fef3c7', color: '#92400e' },
  'Manager': { bg: '#dbeafe', color: '#1e40af' },
  'Operator': { bg: '#dcfce7', color: '#166534' },
  'Viewer': { bg: '#f3e8ff', color: '#6b21a8' },
};

// Helper: extract role name string from a role (object or string)
const getRoleName = ( role ) =>
{
  if ( typeof role === 'string' ) return role;
  return role?.role_name || role?.name || '';
};

// ── Component ───────────────────────────────────────────────────────────────────

export default function UserManagement ()
{
  const { isSuperAdmin, user: currentUser } = useAuth();

  const [ users, setUsers ] = useState( [] );
  const [ companies, setCompanies ] = useState( [] );
  const [ loading, setLoading ] = useState( true );
  const [ search, setSearch ] = useState( '' );
  const [ modalOpen, setModalOpen ] = useState( false );
  const [ deactivateModal, setDeactivateModal ] = useState( null );
  const [ editingUser, setEditingUser ] = useState( null );
  const [ form, setForm ] = useState( { ...emptyUser } );
  const [ saving, setSaving ] = useState( false );
  const [ permissions, setPermissions ] = useState( [] );
  const [ allRoles, setAllRoles ] = useState( [] );

  // Build available roles: use API roles if loaded, fall back to defaults
  const AVAILABLE_ROLES = useMemo( () =>
  {
    if ( allRoles.length > 0 )
    {
      return allRoles
        .filter( r => r.role_name !== 'Super Admin' || isSuperAdmin )
        .map( r =>
        {
          const def = DEFAULT_ROLES.find( d => d.key === r.role_name );
          return { key: r.role_name, label: r.role_name, desc: r.description || def?.desc || '' };
        } );
    }
    return DEFAULT_ROLES.filter( role => !role.superAdminOnly || isSuperAdmin );
  }, [ allRoles, isSuperAdmin ] );

  // ── Fetch data ──────────────────────────────────────────────────────────────

  const fetchUsers = useCallback( async () =>
  {
    setLoading( true );
    try
    {
      const res = await userAPI.getAll();
      setUsers( res.data?.users || res.data || [] );
    } catch ( err )
    {
      toast.error( 'Failed to load users' );
    } finally
    {
      setLoading( false );
    }
  }, [] );

  const fetchCompanies = useCallback( async () =>
  {
    try
    {
      const res = await companyAPI.getAll();
      setCompanies( res.data?.companies || res.data || [] );
    } catch
    {
      // ignore if no companies endpoint
    }
  }, [] );

  const fetchPermissions = useCallback( async () =>
  {
    try
    {
      const res = await userAPI.getPermissions();
      setPermissions( res.data?.permissions || res.data || [] );
    } catch
    {
      // ignore
    }
  }, [] );

  const fetchRoles = useCallback( async () =>
  {
    try
    {
      const res = await userAPI.getRoles();
      setAllRoles( res.data?.roles || res.data || [] );
    } catch
    {
      // ignore
    }
  }, [] );

  useEffect( () =>
  {
    fetchUsers();
    fetchCompanies();
    fetchPermissions();
    fetchRoles();
  }, [ fetchUsers, fetchCompanies, fetchPermissions, fetchRoles ] );

  // ── Filtering ────────────────────────────────────────────────────────────────

  const filtered = users.filter( ( u ) =>
  {
    if ( !search.trim() ) return true;
    const q = search.toLowerCase();
    return (
      ( u.username || '' ).toLowerCase().includes( q ) ||
      ( u.full_name || '' ).toLowerCase().includes( q ) ||
      ( u.email || '' ).toLowerCase().includes( q ) ||
      ( u.roles || [] ).some( ( r ) => getRoleName( r ).toLowerCase().includes( q ) )
    );
  } );

  // ── Modal handlers ──────────────────────────────────────────────────────────

  const openCreate = () =>
  {
    setEditingUser( null );
    setForm( { ...emptyUser } );
    setModalOpen( true );
  };

  const openEdit = ( user ) =>
  {
    setEditingUser( user );
    setForm( {
      username: user.username || '',
      email: user.email || '',
      password: '',
      full_name: user.full_name || '',
      roles: ( user.roles || [] ).map( ( r ) => getRoleName( r ) ),
      company_ids: ( user.companies || [] ).map( ( c ) => c.id ),
    } );
    setModalOpen( true );
  };

  const closeModal = () =>
  {
    setModalOpen( false );
    setEditingUser( null );
    setForm( { ...emptyUser } );
  };

  // ── Save ────────────────────────────────────────────────────────────────────

  const handleSave = async ( e ) =>
  {
    e.preventDefault();
    if ( !form.username.trim() || !form.email.trim() )
    {
      toast.error( 'Username and email are required' );
      return;
    }
    if ( !editingUser && !form.password.trim() )
    {
      toast.error( 'Password is required for new users' );
      return;
    }
    if ( form.roles.length === 0 )
    {
      toast.error( 'Please assign at least one role' );
      return;
    }

    setSaving( true );
    try
    {
      const payload = { ...form };
      // Don't send empty password on edit
      if ( editingUser && !payload.password )
      {
        delete payload.password;
      }
      if ( payload.company_id === '' )
      {
        delete payload.company_id;
      }
      // Convert role names to role_ids for the backend
      const role_ids = ( payload.roles || [] ).map( ( roleName ) =>
      {
        const found = allRoles.find( ( r ) => r.role_name === roleName );
        return found ? found.id : null;
      } ).filter( Boolean );
      payload.role_ids = role_ids;
      // Super Admin gets all companies access — send empty array so backend doesn't clear assignments
      const isSuperAdminRole = payload.roles.includes( 'Super Admin' );
      payload.company_ids = isSuperAdminRole ? [] : ( payload.company_ids || [] ).map( Number );
      delete payload.roles;

      if ( editingUser )
      {
        await userAPI.update( editingUser.id, payload );
        toast.success( 'User updated successfully' );
      } else
      {
        await userAPI.create( payload );
        toast.success( 'User created successfully' );
      }
      closeModal();
      fetchUsers();
    } catch ( err )
    {
      toast.error( err.response?.data?.error || 'Failed to save user' );
    } finally
    {
      setSaving( false );
    }
  };

  // ── Deactivate ──────────────────────────────────────────────────────────────

  const handleDeactivate = async () =>
  {
    if ( !deactivateModal ) return;
    try
    {
      await userAPI.update( deactivateModal.id, { is_active: false } );
      toast.success( 'User deactivated' );
      setDeactivateModal( null );
      fetchUsers();
    } catch ( err )
    {
      toast.error( err.response?.data?.error || 'Failed to deactivate user' );
    }
  };

  // ── Role toggle ─────────────────────────────────────────────────────────────

  const isSuperAdminSelected = form.roles.includes( 'Super Admin' );

  const toggleRole = ( roleKey ) =>
  {
    setForm( ( prev ) =>
    {
      const has = prev.roles.includes( roleKey );

      // If toggling Super Admin ON → set only Super Admin (clear others)
      if ( roleKey === 'Super Admin' && !has )
      {
        return { ...prev, roles: [ 'Super Admin' ] };
      }

      // If toggling Super Admin OFF → just remove it
      if ( roleKey === 'Super Admin' && has )
      {
        return { ...prev, roles: [] };
      }

      // If another role is toggled while Super Admin is active → ignore
      if ( prev.roles.includes( 'Super Admin' ) )
      {
        return prev;
      }

      return {
        ...prev,
        roles: has
          ? prev.roles.filter( ( r ) => r !== roleKey )
          : [ ...prev.roles, roleKey ],
      };
    } );
  };

  // ── Field helpers ────────────────────────────────────────────────────────────

  const updateField = ( field, value ) =>
  {
    setForm( ( prev ) => ( { ...prev, [ field ]: value } ) );
  };

  // ── Render helpers ──────────────────────────────────────────────────────────

  const renderRoleBadges = ( roles ) =>
  {
    if ( !roles || roles.length === 0 )
    {
      return <span style={ { color: '#9ca3af', fontSize: 13 } }>No roles</span>;
    }
    return roles.map( ( role, idx ) =>
    {
      const name = getRoleName( role );
      return (
        <span
          key={ name || idx }
          style={ {
            ...styles.roleBadge,
            background: roleColors[ name ]?.bg || '#f3f4f6',
            color: roleColors[ name ]?.color || '#374151',
          } }
        >
          { name }
        </span>
      );
    } );
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div style={ styles.container }>
      {/* Header */ }
      <motion.div
        initial={ { opacity: 0, y: -15 } }
        animate={ { opacity: 1, y: 0 } }
        transition={ { duration: 0.4 } }
        style={ styles.header }
      >
        <div>
          <h1 style={ styles.title }>User Management</h1>
          <p style={ styles.subtitle }>
            Manage system users, roles and permissions
          </p>
        </div>
        <motion.button
          whileHover={ { scale: 1.04 } }
          whileTap={ { scale: 0.96 } }
          style={ styles.addBtn }
          onClick={ openCreate }
        >
          <FiPlus size={ 18 } />
          Add User
        </motion.button>
      </motion.div>

      {/* Search */ }
      <motion.div
        initial={ { opacity: 0, y: -10 } }
        animate={ { opacity: 1, y: 0 } }
        transition={ { duration: 0.3, delay: 0.1 } }
      >
        <div style={ styles.searchWrapper }>
          <FiSearch size={ 18 } style={ styles.searchIcon } />
          <input
            type="text"
            placeholder="Search users..."
            value={ search }
            onChange={ ( e ) => setSearch( e.target.value ) }
            style={ styles.searchInput }
            onFocus={ ( e ) =>
            {
              e.target.style.borderColor = '#10b981';
              e.target.style.boxShadow = '0 0 0 3px rgba(16, 185, 129, 0.12)';
            } }
            onBlur={ ( e ) =>
            {
              e.target.style.borderColor = 'rgba(16, 185, 129, 0.2)';
              e.target.style.boxShadow = 'none';
            } }
          />
        </div>
      </motion.div>

      {/* Permissions summary */ }
      { permissions.length > 0 && (
        <motion.div
          initial={ { opacity: 0, y: -8 } }
          animate={ { opacity: 1, y: 0 } }
          transition={ { delay: 0.15 } }
          style={ {
            ...styles.glassCard,
            padding: '16px 20px',
            marginBottom: 20,
          } }
        >
          <div style={ { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 } }>
            <FiShield size={ 16 } color="#047857" />
            <span style={ { fontSize: 13, fontWeight: 700, color: '#047857', textTransform: 'uppercase', letterSpacing: 0.6 } }>
              System Permissions
            </span>
          </div>
          <div style={ { display: 'flex', flexWrap: 'wrap', gap: 6 } }>
            { permissions.map( ( perm, idx ) => (
              <span
                key={ idx }
                style={ {
                  padding: '3px 10px',
                  borderRadius: 6,
                  fontSize: 11,
                  fontWeight: 500,
                  background: 'rgba(16, 185, 129, 0.08)',
                  color: '#047857',
                  border: '1px solid rgba(16, 185, 129, 0.15)',
                } }
              >
                { typeof perm === 'string' ? perm : perm.name || perm.key }
              </span>
            ) ) }
          </div>
        </motion.div>
      ) }

      {/* Users Table */ }
      <motion.div
        initial={ { opacity: 0, y: 12 } }
        animate={ { opacity: 1, y: 0 } }
        transition={ { duration: 0.35, delay: 0.2 } }
        style={ styles.glassCard }
      >
        { loading ? (
          <div style={ { textAlign: 'center', padding: 60 } }>
            <div
              style={ {
                width: 40,
                height: 40,
                border: '3px solid rgba(16, 185, 129, 0.2)',
                borderTopColor: '#10b981',
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite',
                margin: '0 auto 16px',
              } }
            />
            <p style={ { color: '#6b7280', fontSize: 14 } }>Loading users...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div style={ styles.emptyState }>
            <FiUsers size={ 48 } style={ { marginBottom: 16, opacity: 0.4 } } />
            <p style={ { fontSize: 16, fontWeight: 600, marginBottom: 4 } }>
              { search ? 'No users match your search' : 'No users found' }
            </p>
            <p style={ { fontSize: 13 } }>
              { search
                ? 'Try adjusting your search query'
                : 'Click "Add User" to create a new user' }
            </p>
          </div>
        ) : (
          <div style={ { overflowX: 'auto' } }>
            <table style={ styles.table }>
              <thead>
                <tr>
                  <th style={ styles.th }>User</th>
                  <th style={ styles.th }>Email</th>
                  <th style={ styles.th }>Roles</th>
                  <th style={ styles.th }>Company</th>
                  <th style={ styles.th }>Status</th>
                  <th style={ { ...styles.th, textAlign: 'center' } }>Actions</th>
                </tr>
              </thead>
              <tbody>
                { filtered.map( ( user, idx ) => (
                  <motion.tr
                    key={ user.id }
                    initial={ { opacity: 0, y: 8 } }
                    animate={ { opacity: 1, y: 0 } }
                    transition={ { delay: idx * 0.03 } }
                    onMouseEnter={ ( e ) =>
                      ( e.currentTarget.style.background = 'rgba(240, 253, 244, 0.5)' )
                    }
                    onMouseLeave={ ( e ) =>
                      ( e.currentTarget.style.background = 'transparent' )
                    }
                  >
                    <td style={ styles.td }>
                      <div style={ { display: 'flex', alignItems: 'center', gap: 10 } }>
                        <div
                          style={ {
                            width: 36,
                            height: 36,
                            borderRadius: 10,
                            background: 'linear-gradient(135deg, #059669, #34d399)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontWeight: 700,
                            fontSize: 14,
                            flexShrink: 0,
                          } }
                        >
                          { ( user.full_name || user.username || '?' )
                            .charAt( 0 )
                            .toUpperCase() }
                        </div>
                        <div>
                          <div style={ { fontWeight: 600, color: '#064e3b', fontSize: 14 } }>
                            { user.full_name || user.username }
                          </div>
                          <div style={ { fontSize: 12, color: '#9ca3af' } }>
                            @{ user.username }
                          </div>
                        </div>
                      </div>
                    </td>
                    <td style={ { ...styles.td, color: '#059669' } }>{ user.email }</td>
                    <td style={ styles.td }>{ renderRoleBadges( user.roles ) }</td>
                    <td style={ styles.td }>
                      { ( user.roles || [] ).some( r => getRoleName( r ) === 'Super Admin' ) ? (
                        <span style={ {
                          ...styles.roleBadge,
                          background: '#fce7f3',
                          color: '#9d174d',
                        } }>All Companies</span>
                      ) : ( user.companies && user.companies.length > 0 ) ? (
                        user.companies.map( ( c ) => (
                          <span key={ c.id } style={ {
                            ...styles.roleBadge,
                            background: 'rgba(16, 185, 129, 0.08)',
                            color: '#047857',
                          } }>
                            { c.exporter_name || `#${ c.id }` }
                          </span>
                        ) )
                      ) : (
                        <span style={ { color: '#9ca3af' } }>--</span>
                      ) }
                    </td>
                    <td style={ styles.td }>
                      <span
                        style={ {
                          ...styles.statusBadge,
                          background:
                            user.is_active === false
                              ? 'rgba(239, 68, 68, 0.1)'
                              : 'rgba(16, 185, 129, 0.1)',
                          color:
                            user.is_active === false ? '#dc2626' : '#059669',
                        } }
                      >
                        { user.is_active === false ? 'Inactive' : 'Active' }
                      </span>
                    </td>
                    <td style={ { ...styles.td, textAlign: 'center', whiteSpace: 'nowrap' } }>
                      { ( () =>
                      {
                        const userIsSuperAdmin = ( user.roles || [] ).some( r => getRoleName( r ) === 'Super Admin' );
                        return (
                          <>
                            <button
                              title={ userIsSuperAdmin ? "Super Admin cannot be edited" : "Edit" }
                              onClick={ () => !userIsSuperAdmin && openEdit( user ) }
                              disabled={ userIsSuperAdmin }
                              style={ {
                                ...styles.actionBtn,
                                background: userIsSuperAdmin ? '#f3f4f6' : 'rgba(16, 185, 129, 0.1)',
                                color: userIsSuperAdmin ? '#9ca3af' : '#059669',
                                cursor: userIsSuperAdmin ? 'not-allowed' : 'pointer',
                              } }
                              onMouseEnter={ ( e ) =>
                              {
                                if ( !userIsSuperAdmin ) e.currentTarget.style.background = 'rgba(16, 185, 129, 0.2)';
                              } }
                              onMouseLeave={ ( e ) =>
                              {
                                e.currentTarget.style.background = userIsSuperAdmin ? '#f3f4f6' : 'rgba(16, 185, 129, 0.1)';
                              } }
                            >
                              <FiEdit2 size={ 15 } />
                            </button>
                            { user.is_active !== false &&
                              user.id !== currentUser?.id &&
                              !userIsSuperAdmin && (
                                <button
                                  title="Deactivate"
                                  onClick={ () => setDeactivateModal( user ) }
                                  style={ {
                                    ...styles.actionBtn,
                                    background: 'rgba(239, 68, 68, 0.1)',
                                    color: '#dc2626',
                                  } }
                                  onMouseEnter={ ( e ) =>
                                  ( e.currentTarget.style.background =
                                    'rgba(239, 68, 68, 0.2)' )
                                  }
                                  onMouseLeave={ ( e ) =>
                                  ( e.currentTarget.style.background =
                                    'rgba(239, 68, 68, 0.1)' )
                                  }
                                >
                                  <FiUserX size={ 15 } />
                                </button>
                              ) }
                          </>
                        );
                      } )() }
                    </td>
                  </motion.tr>
                ) ) }
              </tbody>
            </table>
          </div>
        ) }
      </motion.div>

      {/* ── Create / Edit Modal ──────────────────────────────────────────────── */ }
      <AnimatePresence>
        { modalOpen && (
          <motion.div
            initial={ { opacity: 0 } }
            animate={ { opacity: 1 } }
            exit={ { opacity: 0 } }
            style={ styles.overlay }
            onClick={ closeModal }
          >
            <motion.div
              initial={ { opacity: 0, scale: 0.93, y: 20 } }
              animate={ { opacity: 1, scale: 1, y: 0 } }
              exit={ { opacity: 0, scale: 0.93, y: 20 } }
              transition={ { duration: 0.25 } }
              style={ styles.modal }
              onClick={ ( e ) => e.stopPropagation() }
            >
              <button
                onClick={ closeModal }
                style={ {
                  position: 'absolute',
                  top: 16,
                  right: 16,
                  background: 'rgba(239, 68, 68, 0.08)',
                  border: 'none',
                  borderRadius: 8,
                  width: 36,
                  height: 36,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  color: '#dc2626',
                } }
              >
                <FiX size={ 18 } />
              </button>

              <div style={ styles.modalTitle }>
                <div
                  style={ {
                    width: 40,
                    height: 40,
                    borderRadius: 12,
                    background: 'linear-gradient(135deg, #059669, #10b981)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  } }
                >
                  { editingUser ? (
                    <FiEdit2 size={ 18 } color="white" />
                  ) : (
                    <FiPlus size={ 18 } color="white" />
                  ) }
                </div>
                { editingUser ? 'Edit User' : 'Create New User' }
              </div>

              <form onSubmit={ handleSave }>
                <div style={ styles.fieldGrid }>
                  {/* Username */ }
                  <div>
                    <label style={ styles.fieldLabel }>Username *</label>
                    <div style={ { position: 'relative' } }>
                      <FiUser
                        size={ 16 }
                        style={ {
                          position: 'absolute',
                          left: 12,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          color: '#059669',
                        } }
                      />
                      <input
                        type="text"
                        value={ form.username }
                        onChange={ ( e ) => updateField( 'username', e.target.value ) }
                        style={ { ...styles.fieldInput, paddingLeft: 36 } }
                        placeholder="Username"
                        required
                      />
                    </div>
                  </div>

                  {/* Email */ }
                  <div>
                    <label style={ styles.fieldLabel }>Email *</label>
                    <div style={ { position: 'relative' } }>
                      <FiMail
                        size={ 16 }
                        style={ {
                          position: 'absolute',
                          left: 12,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          color: '#059669',
                        } }
                      />
                      <input
                        type="email"
                        value={ form.email }
                        onChange={ ( e ) => updateField( 'email', e.target.value ) }
                        style={ { ...styles.fieldInput, paddingLeft: 36 } }
                        placeholder="email@example.com"
                        required
                      />
                    </div>
                  </div>

                  {/* Password */ }
                  <div>
                    <label style={ styles.fieldLabel }>
                      Password { editingUser ? '(leave blank to keep)' : '*' }
                    </label>
                    <div style={ { position: 'relative' } }>
                      <FiLock
                        size={ 16 }
                        style={ {
                          position: 'absolute',
                          left: 12,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          color: '#059669',
                        } }
                      />
                      <input
                        type="password"
                        value={ form.password }
                        onChange={ ( e ) => updateField( 'password', e.target.value ) }
                        style={ { ...styles.fieldInput, paddingLeft: 36 } }
                        placeholder={
                          editingUser
                            ? 'Leave blank to keep current'
                            : 'Enter password'
                        }
                        required={ !editingUser }
                      />
                    </div>
                  </div>

                  {/* Full Name */ }
                  <div>
                    <label style={ styles.fieldLabel }>Full Name</label>
                    <input
                      type="text"
                      value={ form.full_name }
                      onChange={ ( e ) => updateField( 'full_name', e.target.value ) }
                      style={ styles.fieldInput }
                      placeholder="Full name"
                    />
                  </div>
                </div>

                {/* Company Assignment */ }
                <div style={ { marginTop: 16 } }>
                  <label style={ { ...styles.fieldLabel, marginBottom: 12 } }>
                    <FiBriefcase
                      size={ 13 }
                      style={ { marginRight: 6, verticalAlign: 'middle' } }
                    />
                    Company Assignment
                  </label>
                  { form.roles.includes( 'Super Admin' ) ? (
                    <div style={ {
                      padding: '14px 16px',
                      borderRadius: 12,
                      background: 'linear-gradient(135deg, rgba(157, 23, 77, 0.06), rgba(252, 231, 243, 0.5))',
                      border: '2px solid rgba(157, 23, 77, 0.15)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                    } }>
                      <FiShield size={ 18 } color="#9d174d" />
                      <div>
                        <div style={ { fontSize: 14, fontWeight: 600, color: '#9d174d' } }>
                          All Companies Access
                        </div>
                        <div style={ { fontSize: 12, color: '#9ca3af' } }>
                          Super Admin automatically has access to all companies
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div style={ {
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                      gap: 10,
                      maxHeight: 200,
                      overflowY: 'auto',
                      padding: 2,
                    } }>
                      { companies.length === 0 ? (
                        <div style={ { color: '#9ca3af', fontSize: 13, padding: '8px 0' } }>
                          No companies available
                        </div>
                      ) : companies.map( ( c ) =>
                      {
                        const active = ( form.company_ids || [] ).includes( c.id );
                        return (
                          <div
                            key={ c.id }
                            onClick={ () =>
                            {
                              setForm( ( prev ) =>
                              {
                                const ids = prev.company_ids || [];
                                const has = ids.includes( c.id );
                                return {
                                  ...prev,
                                  company_ids: has
                                    ? ids.filter( ( id ) => id !== c.id )
                                    : [ ...ids, c.id ],
                                };
                              } );
                            } }
                            style={ {
                              ...styles.roleCheckbox,
                              ...( active ? styles.roleCheckboxActive : {} ),
                            } }
                          >
                            <div style={ {
                              width: 20, height: 20, borderRadius: 6,
                              border: active ? '2px solid #10b981' : '2px solid #d1d5db',
                              background: active ? 'linear-gradient(135deg, #059669, #10b981)' : 'white',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              flexShrink: 0, marginTop: 1,
                            } }>
                              { active && <FiCheck size={ 12 } color="white" /> }
                            </div>
                            <div>
                              <div style={ { fontSize: 14, fontWeight: 600, color: active ? '#064e3b' : '#374151' } }>
                                { c.exporter_name || c.name || `Company #${ c.id }` }
                              </div>
                              { c.iec && (
                                <div style={ { fontSize: 12, color: '#9ca3af' } }>IEC: { c.iec }</div>
                              ) }
                            </div>
                          </div>
                        );
                      } ) }
                    </div>
                  ) }
                </div>

                {/* Role Selection */ }
                <div style={ { marginTop: 20 } }>
                  <label style={ { ...styles.fieldLabel, marginBottom: 12 } }>
                    <FiShield
                      size={ 13 }
                      style={ { marginRight: 6, verticalAlign: 'middle' } }
                    />
                    Roles *
                  </label>
                  <div
                    style={ {
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                      gap: 10,
                    } }
                  >
                    { AVAILABLE_ROLES.map( ( role ) =>
                    {
                      const active = form.roles.includes( role.key );
                      const disabled = isSuperAdminSelected && role.key !== 'Super Admin';
                      return (
                        <div
                          key={ role.key }
                          onClick={ () => !disabled && toggleRole( role.key ) }
                          style={ {
                            ...styles.roleCheckbox,
                            ...( active ? styles.roleCheckboxActive : {} ),
                            ...( disabled ? { opacity: 0.4, cursor: 'not-allowed' } : {} ),
                          } }
                        >
                          <div
                            style={ {
                              width: 20,
                              height: 20,
                              borderRadius: 6,
                              border: active
                                ? '2px solid #10b981'
                                : '2px solid #d1d5db',
                              background: active
                                ? 'linear-gradient(135deg, #059669, #10b981)'
                                : 'white',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                              marginTop: 1,
                            } }
                          >
                            { active && <FiCheck size={ 12 } color="white" /> }
                          </div>
                          <div>
                            <div
                              style={ {
                                fontSize: 14,
                                fontWeight: 600,
                                color: active ? '#064e3b' : '#374151',
                              } }
                            >
                              { role.label }
                            </div>
                            <div style={ { fontSize: 12, color: '#9ca3af' } }>
                              { disabled ? 'Not needed — Super Admin has full access' : role.desc }
                            </div>
                          </div>
                        </div>
                      );
                    } ) }
                  </div>
                </div>

                {/* Buttons */ }
                <div
                  style={ {
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 12,
                    marginTop: 28,
                  } }
                >
                  <button type="button" style={ styles.cancelBtn } onClick={ closeModal }>
                    Cancel
                  </button>
                  <motion.button
                    whileHover={ { scale: 1.03 } }
                    whileTap={ { scale: 0.97 } }
                    type="submit"
                    style={ {
                      ...styles.saveBtn,
                      opacity: saving ? 0.7 : 1,
                      cursor: saving ? 'not-allowed' : 'pointer',
                    } }
                    disabled={ saving }
                  >
                    <FiSave size={ 16 } />
                    { saving
                      ? 'Saving...'
                      : editingUser
                        ? 'Update User'
                        : 'Create User' }
                  </motion.button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        ) }
      </AnimatePresence>

      {/* ── Deactivate Confirmation Modal ─────────────────────────────────────── */ }
      <AnimatePresence>
        { deactivateModal && (
          <motion.div
            initial={ { opacity: 0 } }
            animate={ { opacity: 1 } }
            exit={ { opacity: 0 } }
            style={ styles.overlay }
            onClick={ () => setDeactivateModal( null ) }
          >
            <motion.div
              initial={ { opacity: 0, scale: 0.9 } }
              animate={ { opacity: 1, scale: 1 } }
              exit={ { opacity: 0, scale: 0.9 } }
              style={ {
                ...styles.modal,
                maxWidth: 440,
                textAlign: 'center',
                padding: '40px 32px',
              } }
              onClick={ ( e ) => e.stopPropagation() }
            >
              <div
                style={ {
                  width: 60,
                  height: 60,
                  borderRadius: 16,
                  background: 'rgba(239, 68, 68, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 20px',
                } }
              >
                <FiAlertTriangle size={ 28 } color="#dc2626" />
              </div>
              <h3
                style={ {
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: 20,
                  fontWeight: 700,
                  color: '#1f2937',
                  marginBottom: 8,
                } }
              >
                Deactivate User?
              </h3>
              <p style={ { color: '#6b7280', fontSize: 14, marginBottom: 28 } }>
                Are you sure you want to deactivate{ ' ' }
                <strong style={ { color: '#064e3b' } }>
                  { deactivateModal.full_name || deactivateModal.username }
                </strong>
                ? They will no longer be able to log in.
              </p>
              <div style={ { display: 'flex', gap: 12, justifyContent: 'center' } }>
                <button
                  style={ styles.cancelBtn }
                  onClick={ () => setDeactivateModal( null ) }
                >
                  Cancel
                </button>
                <motion.button
                  whileHover={ { scale: 1.03 } }
                  whileTap={ { scale: 0.97 } }
                  style={ {
                    ...styles.saveBtn,
                    background: 'linear-gradient(135deg, #dc2626, #ef4444)',
                    boxShadow: '0 4px 15px rgba(239, 68, 68, 0.3)',
                  } }
                  onClick={ handleDeactivate }
                >
                  <FiUserX size={ 16 } />
                  Deactivate
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        ) }
      </AnimatePresence>
    </div>
  );
}
